import MomentUtils from '@date-io/moment';
import { createTheme, MuiThemeProvider } from '@material-ui/core/styles';
import 'assets/vendors/style';
import defaultTheme from 'containers/themes/defaultTheme';
import { decode } from 'jsonwebtoken-esm';
import AppLocale from 'lngProvider';
import arLocalTranslations from 'lngProvider/defaultApp-ar.json';
import enLocalTranslations from 'lngProvider/defaultApp-en.json';
import frLocalTranslations from 'lngProvider/defaultApp-fr.json';
import trLocalTranslations from 'lngProvider/defaultApp-tr.json';
import { MuiPickersUtilsProvider } from 'material-ui-pickers';
import React, { Component } from 'react';
import ReactGA from 'react-ga';
import { IntlProvider } from 'react-intl';
import { connect } from 'react-redux';

import { ToastContainer } from 'react-toastify';

import { setSocketId, userSignOut } from 'actions/Auth';
import RTL from 'util/RTL';
import Router from './modules/Router/Router';
import axiosInstance from './modules/shared/api/axiosInstance';
import devicesAxiosInstance from './modules/shared/api/devicesAxiosInstance';
import { urlBase64ToUint8Array } from './modules/shared/util/urlBase64ToUint8Array';

import { getNotifications, processNotification } from 'actions/Notifications';
import { TranslationAPI } from './modules/CompanyManagement/Translations/TranslationAPI';
import {
    apiRequest,
    handlePromiseError,
} from './modules/shared/api/apiRequest';

import { ConfigProvider } from 'antd';

import { registerLicense } from '@syncfusion/ej2-base';
import { getUserNotificationsToken, onMessageHandler } from 'firebase.config';
import TagManager from 'react-gtm-module';
import GoogleTagManager from './GoogleTagManager';
import DevtoolCheckbox from './modules/devtools/DevtoolCheckbox';
import { Spinner } from './modules/shared/components/Spinner/Spinner';
import { socket } from './modules/shared/socket/socket';
import {
    checkNotificationsPermission,
    getNotificationSubscriberToken,
    getSavedPermissionStatus,
    setNotificationSubscriberToken,
} from './modules/shared/util/notificationsUtils';

//import sentry
import * as Sentry from '@sentry/react';
import { createBrowserHistory } from 'history/cjs/history.min';
import { __ON_PREMISE__, __ON_PRODUCTION__ } from 'index';
import { matchPath } from 'react-router-dom';
import IntlMessages from 'util/IntlMessages';
import ChatComponent from './ChatComponent/ChatComponent';
import routes from './modules/Router/routes';
import ClearCacheButton from './modules/shared/error/components/ClearCacheButton';
import { ErrorBoundary } from './modules/shared/error/ErrorBoundary';
import ErrorBoundaryFallBack from './modules/shared/error/ErrorBoundaryFallBack';
import showNotificationMessage from './modules/shared/util/NotificationMessage';
import { isDevToolVisible } from './utils/helper';
import { ChatComponentAPI } from './ChatComponent/ChatComponentAPI';
import { initZohoSalesIQ } from './utils/initZohoSalesIQ';
import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import { withRouter } from '../hoc/withRouter';

const history = createBrowserHistory();
class App extends Component {
    constructor(props) {
        super(props);
        this.state = {
            translations: [],
            tagManagerArgs: {
                gtmId: 'GTM-PV939X56',
            },
            notificationService: localStorage.getItem('notificationService'),
            syncfusionKey: import.meta.env.VITE_SYNCFUSION_KEY,
        };
    }

    async componentDidMount() {
        registerLicense(this.state.syncfusionKey);

        if (__ON_PREMISE__ || this.state.notificationService === 'socket') {
            if (this.state.notificationService !== 'socket') {
                localStorage.setItem('notificationService', 'socket');
                this.setState({
                    notificationService: 'socket',
                });
            }
            socket.on('connect', () => this.props.setSocketId(socket.id));
        }

        //gtag
        if (__ON_PRODUCTION__ && !window.location.hostname.includes('alexr')) {
            window.addEventListener(
                'load',
                () => {
                    ReactGA.initialize('UA-110457752-3');
                    ReactGA.pageview(
                        window.location.pathname + window.location.search,
                    );
                },
                false,
            );
        }

        // Check the Notifications Permission
        checkNotificationsPermission();

        // Initialize Google Tag Manager
        TagManager.initialize(this.state.tagManagerArgs);

        // Sync factoryId from localStorage to URL
        this.syncUrlFactoryId();

        if (this.props.authUser) {
            const user = JSON.parse(this.props.authUser);
            const token = user.token;
            const tokenExpiry = decode(token) && decode(token).exp;
            const now = Date.now() / 1000;

            const userId = user._id;

            if (!tokenExpiry || tokenExpiry < now) {
                this.props.userSignOut();
                return;
            }

            this.checkIsTranslationUpdated();

            this.setupZohoSalesIQ();

            if (__ON_PREMISE__ || this.state.notificationService === 'socket') {
                socket.emit('subscribe_notification', {
                    userId,
                });

                if ('serviceWorker' in navigator) {
                    this.subscribeToPushNotificationWithSocket(userId);
                }

                this.props.getNotifications(this.props.slice);

                socket.on('notifications', (notification) =>
                    this.props.processNotification(notification),
                );
            } else {
                this.handleNotificationsWithFirebase(userId);
            }

            const sentryKey = import.meta.env.VITE_SENTRY_KEY;
            const dataDogApplicationId = import.meta.env
                .VITE_APPLICATIONID_DATA_DOG;
            const dataDogClientToken = import.meta.env
                .VITE_CLIENT_TOKEN_DATA_DOG;

            if (__ON_PRODUCTION__) {
                Sentry.init({
                    dsn: sentryKey,
                    integrations: [
                        Sentry.replayIntegration(),
                        Sentry.reactRouterV5BrowserTracingIntegration({
                            history,
                            routes,
                            matchPath,
                        }),
                    ],
                    tracesSampleRate: 1.0,
                    replaysSessionSampleRate: 0.1,
                    replaysOnErrorSampleRate: 1.0,
                    beforeSend: (event, hint) => {
                        const { response } = hint.originalException;
                        if (
                            response?.status &&
                            response.status >= 400 &&
                            response.status < 402
                        ) {
                            return null;
                        }

                        return event;
                    },
                });

                Sentry.setUser({
                    id: user._id,
                    email: user.email,
                    company: user.company,
                    factory: user.factory,
                    token: user.token,
                });

                //Data_Dog

                datadogRum.init({
                    applicationId: dataDogApplicationId,
                    clientToken: dataDogClientToken,
                    site: 'datadoghq.eu',
                    service: 'garment-react',
                    env: 'PRODUCTION',
                    version: '3.0.2',
                    sessionSampleRate: 100, // Still track all sessions
                    sessionReplaySampleRate: 100,
                    trackInteractions: true,
                    defaultPrivacyLevel: 'mask-user-input',
                });

                datadogLogs.init({
                    clientToken: dataDogClientToken,
                    site: 'datadoghq.eu',
                    service: 'garment-react',
                    env: 'PRODUCTION',
                    forwardErrorsToLogs: true,
                    sampleRate: 100,
                });

                window.addEventListener('error', () => {
                    datadogRum.startSessionReplayRecording();
                });

                window.addEventListener('unhandledrejection', () => {
                    datadogRum.startSessionReplayRecording();
                });

                datadogRum.setUser({
                    id: user._id,
                    email: user.email,
                    name: user.name,
                });

                datadogLogs.setUser({
                    id: user._id,
                    email: user.email,
                    name: user.name,
                });
            }
        } else {
            this.setState({ isTranslationLoading: false });
        }

        const bodyEl = document.getElementById('body');
        bodyEl.classList.add(
            `${
                this.props.locale.locale === 'ar' ? 'arabicFont' : 'englishFont'
            }`,
        );
        document.documentElement.lang = this.props.locale.locale;
        this.handleHTMLDirection();
    }

    async componentDidUpdate(prevProps) {
        if (prevProps.locale.locale !== this.props.locale.locale) {
            const bodyEl = document.getElementById('body');

            this.props.locale.locale === 'ar'
                ? bodyEl.classList.remove('englishFont')
                : bodyEl.classList.remove('arabicFont');

            bodyEl.classList.add(
                `${
                    this.props.locale.locale === 'ar'
                        ? 'arabicFont'
                        : 'englishFont'
                }`,
            );
            document.documentElement.lang = this.props.locale.locale;
        }
        this.handleHTMLDirection();

        if (prevProps.location !== this.props.location) {
            setTimeout(() => {
                window.onbeforeunload = false;
            }, 0);
        }

        if (this.props.authUser !== prevProps.authUser) {
            if (this.props.authUser) {
                this.setupZohoSalesIQ();
            } else {
                if (window.$zoho && window.$zoho.salesiq) {
                    window.$zoho = null;
                }
                window.location.reload();
            }
        }

        if (
            (this.props.authUser !== prevProps.authUser &&
                this.props.authUser &&
                this.state.translations.length === 0) ||
            prevProps.location.pathname === '/reload'
        ) {
            this.checkIsTranslationUpdated();
            if (
                !__ON_PREMISE__ &&
                this.state.notificationService === 'firebase'
            ) {
                const user = JSON.parse(this.props.authUser);
                const userId = user._id;

                this.handleNotificationsWithFirebase(userId);
            }
        }
    }

    handleNotificationsWithFirebase(userId) {
        try {
            const notificationPermission = getSavedPermissionStatus();
            const notificationToken = getNotificationSubscriberToken();
            if (notificationPermission === 'granted') {
                !notificationToken &&
                    this.subscribeToPushNotificationWithFirebase(userId);

                this.props.getNotifications(this.props.slice);

                onMessageHandler((payload) => {
                    this.props.processNotification(payload.data);
                });
            }
        } catch (error) {
            localStorage.setItem('notificationService', 'socket');
            this.setState({ notificationService: 'socket' });
            console.error(error);
        }
    }

    handleHTMLDirection() {
        const html = document.querySelector('html');
        html.setAttribute(
            'dir',
            this.props.locale.locale === 'ar' ? 'rtl' : 'ltr',
        );
    }

    syncUrlFactoryId = () => {
        try {
            const user = this.props.authUser
                ? JSON.parse(this.props.authUser)
                : null;

            if (!user) return;

            const factoryId = String(user.factory || '');

            if (!factoryId) return;

            const pathname =
                this.props.location?.pathname || window.location.pathname;
            const pathSegments = pathname.split('/').filter(Boolean);
            const urlFactoryIdParam = pathSegments[0];

            if (isNaN(urlFactoryIdParam) || urlFactoryIdParam === '404' || urlFactoryIdParam === '403') return;

            if (urlFactoryIdParam && urlFactoryIdParam !== factoryId) {
                pathSegments[0] = factoryId;
                const newPathname = '/' + pathSegments.join('/');
                this.props.history.replace(newPathname);
            }
        } catch (error) {
            console.error('Error syncing factoryId to URL:', error);
        }
    };

    subscribeToPushNotificationWithFirebase = async (userId) => {
        let userNotificationsToken = null;
        // showNotificationMessage wont fire the toast if the ui hasn't rendered
        await getUserNotificationsToken()
            .then((currentToken) => {
                if (currentToken) {
                    userNotificationsToken = currentToken;
                    setNotificationSubscriberToken(currentToken);
                    console.log(
                        'Notifications permission is granted and user token is saved.',
                    );
                    showNotificationMessage(
                        'success',
                        <IntlMessages id='notification.token.successMessage' />,
                    );
                } else {
                    console.log(
                        'No registration token available. Enable the permission to generate one.',
                    );
                    showNotificationMessage(
                        'error',
                        <IntlMessages id='notification.token.warningMessage' />,
                    );
                }
            })
            .catch((err) => {
                console.error(
                    'An error occurred while retrieving fcm notification token.',
                    err.message,
                );
                showNotificationMessage(
                    'error',
                    <IntlMessages id='notification.token.errorMessage' />,
                );
            });

        if (userNotificationsToken) {
            const userSubscriptionParams = {
                token: userNotificationsToken,
                subscriberId: userId,
                type: 'user',
            };

            //send push notification
            console.log('Subscribing to notifications...');
            await axiosInstance.post(
                'notificationMessage/storeSubscriberToken',
                userSubscriptionParams,
            );

            console.log('Subscribed 🚀🚀');
        }
    };

    subscribeToPushNotificationWithSocket = async (userId) => {
        //register service worker
        console.log('registering service worker...');
        const register = await navigator.serviceWorker.register('/worker.js', {
            scope: '/',
        });
        console.log('service worker registered...');

        const webPushApiKey =
            'BIspO6pVaT4unZkdPvJGQsf9nH_fHZ8BB5ReRPJsyjXZj_EUrkZKb4bHHIGnXRFLpvmaqMukGfD_o9tEOEigk3c';

        let subscription;

        //register push
        console.log('registering push...');
        try {
            subscription = await register.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(webPushApiKey),
            });
        } catch (error) {
            console.log('failed registering', error);
            return;
        }

        console.log('push registered...');

        //send push notification
        console.log('sending subscription...');
        await axiosInstance.post('/notification/pushSubscribe', {
            subscription,
            userId,
        });

        console.log('subscribed 🚀🚀');
    };

    checkIsTranslationUpdated = () => {
        const user = JSON.parse(this.props.authUser);

        const translations =
            (localStorage.getItem('translations') &&
                JSON.parse(localStorage.getItem('translations'))) ||
            [];

        const translationsVersion = translations.version ?? 0;

        this.setState({ translations });

        apiRequest(
            TranslationAPI.isTranslationUpdated,
            user.company,
            translationsVersion,
        )
            .then(({ data }) => {
                if (!data.data.updated) return;
                apiRequest(TranslationAPI.getTranslations, user.company)
                    .then(({ data }) => {
                        const formulatedTranslations =
                            this.reformulateTranslations(data.data);
                        localStorage.setItem(
                            'translations',
                            JSON.stringify(formulatedTranslations, null, 2),
                        );
                        this.setState(
                            { translations: formulatedTranslations },
                            () => {
                                this.setState({ isTranslationLoading: false });
                            },
                        );
                    })
                    .catch(handlePromiseError);
            })
            .catch(handlePromiseError);
    };

    setupZohoSalesIQ = async () => {
        const user = JSON.parse(this.props.authUser);
        try {
            const response = await ChatComponentAPI.getLinesByScope(
                user.company,
                user.factory,
            );

            const productionLinesName =
                response.data.data.productionLinesAndSections?.map(
                    (line) => line.name,
                );
            const scopeNames = user.scope.garment
                ? productionLinesName?.at(0)
                : productionLinesName;

            const userRoles = user?.roles?.map((r) => r.name);

            const visitorInfo = {
                'Company Name ': `${user.company}`,
                'Factory Name': `${user.factoryData.name}`,
                'Role List': `${userRoles}`,
                'Main Role': `${user.mainRole.name}`,
                'scope name': `${scopeNames}`,
                'User Language': `${user.lang}`,
                'Scope type': `${
                    user.scope.garment
                        ? 'Garment'
                        : user.scope.company.length > 0
                          ? 'Company'
                          : user.scope.factory.length > 0
                            ? 'Factory'
                            : 'production line'
                }`,
            };
            initZohoSalesIQ(user, visitorInfo);
        } catch (error) {
            handlePromiseError(error);
        }
    };

    reformulateTranslations = ({ translations, version }) => {
        const en = {};
        const ar = {};
        const fr = {};
        const tr = {};
        const enTooltip = {};
        const arTooltip = {};
        const frTooltip = {};
        const trTooltip = {};
        translations.forEach((item) => {
            en[item.key] = item.value.en;
            ar[item.key] = item.value.ar;
            fr[item.key] = item.value.fr;
            tr[item.key] = item.value.tr;
            enTooltip[item.key] = item.tooltip.en;
            arTooltip[item.key] = item.tooltip.ar;
            frTooltip[item.key] = item.tooltip.fr;
            trTooltip[item.key] = item.tooltip.tr;
        });
        return {
            en,
            ar,
            fr,
            tr,
            enTooltip,
            arTooltip,
            frTooltip,
            trTooltip,
            version,
        };
    };

    render() {
        const {
            locale,
            authUser,
            isDirectionRTL,
            toastOn,
            chatOpenned,
            isLanguageSwitching,
        } = this.props;

        let userLang = '';

        if (authUser) {
            const user = JSON.parse(authUser);
            axiosInstance.defaults.headers.token = user.token;
            devicesAxiosInstance.defaults.headers.token = user.token;
            userLang = user?.systemLanguage?.toLowerCase();
        }

        const fontFamily = `${isDirectionRTL ? 'Cairo' : 'Roboto'}, sans-serif`;
        const direction = isDirectionRTL ? 'rtl' : 'ltr';
        const applyTheme = createTheme({
            ...defaultTheme,
            direction,
            typography: {
                ...defaultTheme.typography,
                fontFamily,
            },
        });

        if (isDirectionRTL) {
            document.body.classList.add('rtl');
        } else {
            document.body.classList.remove('rtl');
        }

        const userTranslations = this.state.translations[userLang];

        const currentAppLocale = AppLocale[locale.locale];

        const translationMap = {
            'en-US': enLocalTranslations,
            'fr-FR': frLocalTranslations,
            'tr-TR': trLocalTranslations,
        };
        const localTranslations =
            translationMap[currentAppLocale?.locale] || arLocalTranslations;

        const isReloading = this.props.location.pathname === '/reload';

        return (authUser && !userTranslations && !isReloading) ||
            isLanguageSwitching ? (
            <div className='app-main w-100 d-flex justify-content-center flex-column align-items-center'>
                <Spinner show={true} />
                <ClearCacheButton />
            </div>
        ) : (
            <MuiThemeProvider theme={applyTheme}>
                <MuiPickersUtilsProvider utils={MomentUtils}>
                    <IntlProvider
                        locale={currentAppLocale?.locale}
                        messages={
                            !authUser ? localTranslations : userTranslations
                        }
                    >
                        <ErrorBoundary
                            fallbackRenderer={({ error, errorInfo }) => (
                                <ErrorBoundaryFallBack
                                    error={error}
                                    errorInfo={errorInfo}
                                />
                            )}
                        >
                            <RTL>
                                <ConfigProvider
                                    direction={isDirectionRTL ? 'rtl' : 'ltr'}
                                >
                                    <div className='app-main'>
                                        <GoogleTagManager />
                                        {authUser && chatOpenned && (
                                            <ChatComponent />
                                        )}
                                        <Router />
                                        {toastOn && (
                                            <ToastContainer
                                                rtl={isDirectionRTL}
                                            />
                                        )}
                                        {isDevToolVisible(
                                            window.location.pathname,
                                        ) && (
                                            <div>
                                                <DevtoolCheckbox />
                                            </div>
                                        )}
                                    </div>
                                </ConfigProvider>
                            </RTL>
                        </ErrorBoundary>
                    </IntlProvider>
                </MuiPickersUtilsProvider>
            </MuiThemeProvider>
        );
    }
}

const mapStateToProps = ({ settings, auth, notifications: { slice } }) => {
    const {
        sideNavColor,
        locale,
        isDirectionRTL,
        chatOpenned,
        isLanguageSwitching,
    } = settings;
    const { authUser, initURL, toastOn } = auth;
    return {
        sideNavColor,
        locale,
        isDirectionRTL,
        authUser,
        initURL,
        toastOn,
        slice,
        chatOpenned,
        isLanguageSwitching,
    };
};

export default withRouter(
    connect(mapStateToProps, {
        userSignOut,
        setSocketId,
        getNotifications,
        processNotification,
    })(App),
);
